import React, { useState } from 'react'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Loading from '../../Components/Loading/LoadingCenterPage';
import LoadRegistration from './LoadData/LoadRegistration';
import Swal from 'sweetalert2';
import { 
    ChevronDownIcon, 
    CalendarIcon, 
    ClipboardDocumentCheckIcon,
    ExclamationCircleIcon,
    ChartBarIcon,
    PresentationChartLineIcon,
    AcademicCapIcon,
    UserGroupIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip } from '@mui/material';

const Report = () => {
    const [reportCategory, setReportCategory] = useState('registration');
    const [viewMode, setViewMode] = useState('position'); 
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    
    const dataRegistration = LoadRegistration()?.filter(v => v.remark !== "ซ้ำ")
    
    const positionJob = React.useMemo(() => {
        return [...new Set(dataRegistration?.map(v => v.positionjob))];
    }, [dataRegistration]);

    const stats = React.useMemo(() => {
        if (!dataRegistration) return { total: 0, pending: 0, completed: 0, percent: 0 };
        const total = dataRegistration.length;
        const completed = dataRegistration.filter(v => v.check === "yes").length;
        const pending = total - completed;
        const percent = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
        return { total, pending, completed, percent };
    }, [dataRegistration]);

    const lineChartData = React.useMemo(() => {
        if (!dataRegistration || !positionJob) return [];
        const years = [...new Set(dataRegistration.map(d => d.timestamp.split("-")[0]))].sort();
        
        return years.map(year => {
            const entry = { name: `ปี ${parseInt(year) + 543}` };
            positionJob.forEach(pos => {
                entry[pos] = dataRegistration.filter(d => 
                    d.timestamp.split("-")[0] === year && d.positionjob === pos
                ).length;
            });
            return entry;
        });
    }, [dataRegistration, positionJob]);

    const chartDataByPosition = React.useMemo(() => {
        if (!dataRegistration || !positionJob) return [];
        return positionJob.map(pos => {
            const related = dataRegistration.filter(v => v.positionjob === pos);
            return {
                name: pos || "ไม่ระบุ",
                "มาใหม่": related.filter(v => v.check !== "yes").length,
                "ติดต่อแล้ว": related.filter(v => v.check === "yes").length,
            };
        }).sort((a, b) => (b["มาใหม่"] + b["ติดต่อแล้ว"]) - (a["มาใหม่"] + a["ติดต่อแล้ว"]));
    }, [dataRegistration, positionJob]);

    const chartDataByYear = React.useMemo(() => {
        if (!dataRegistration) return [];
        const object = {}
        dataRegistration.forEach(trans => {
            const year = trans.timestamp.split("-")[0];
            if (!object[year]) object[year] = { name: `ปี ${parseInt(year) + 543}`, "มาใหม่": 0, "ติดต่อแล้ว": 0 }
            if (trans.check !== "yes") object[year]["มาใหม่"] += 1;
            else object[year]["ติดต่อแล้ว"] += 1;
        });
        return Object.values(object).sort((a, b) => a.name.localeCompare(b.name));
    }, [dataRegistration]);

    const handleViewDetails = (year, position = null) => {
        const yearNum = year.toString();
        let filtered = dataRegistration.filter(d => d.timestamp.split("-")[0] === yearNum);
        
        if (position) {
            filtered = filtered.filter(d => d.positionjob === position);
        }
        
        setSelectedDetails({
            year: parseInt(yearNum) + 543,
            position: position || 'ทั้งหมด',
            data: filtered
        });
        setOpenDialog(true);
    };

    const manageData = React.useMemo(() => {
        try {
            const object = {}
            dataRegistration?.forEach(trans => {
                const year = trans.timestamp.split("-")[0]
                if (!object[year]) object[year] = { transaction: [] }
                object[year].transaction?.push(trans)
            })

            return Object.entries(object).sort((a, b) => b[0] - a[0]).map(([key, { transaction }]) => {
                const totalInYear = transaction?.length;
                const positionsInYear = positionJob.filter(pos => transaction.some(t => t.positionjob === pos));

                return (
                    <Accordion key={key} disableGutters elevation={0} sx={{ mb: 2, borderRadius: '20px !important', border: '1px solid #F1F5F9', overflow: 'hidden' }}>
                        <AccordionSummary expandIcon={<ChevronDownIcon className="h-5 w-5" />} sx={{ backgroundColor: '#F8FAFC', px: 3 }}>
                            <div className="flex justify-between items-center w-full pr-4">
                                <div className="flex items-center gap-2">
                                    <CalendarIcon className="h-5 w-5 text-indigo-500" />
                                    <span className="font-bold text-gray-800 text-lg">ปี {parseInt(key) + 543}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs bg-indigo-600 text-white px-3 py-1 rounded-full font-bold shadow-sm">{totalInYear} ผู้สมัคร</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewDetails(key);
                                        }}
                                        className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-bold hover:bg-blue-200 transition-colors flex items-center gap-1"
                                    >
                                        <EyeIcon className="h-3 w-3" />
                                        ดูรายชื่อ
                                    </button>
                                </div>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <div className="p-4 space-y-3">
                                {positionsInYear.map((pos, i) => {
                                    const count = transaction?.filter(f => f.positionjob === pos)?.length;
                                    const pending = transaction?.filter(f => f.positionjob === pos && f.check !== "yes")?.length;
                                    const completed = count - pending;
                                    
                                    return (
                                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="flex-1">
                                                <p className="font-bold text-gray-900 text-sm">{pos || "ไม่ระบุตำแหน่ง"}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Chip 
                                                        label={`รอติดต่อ: ${pending}`}
                                                        size="small"
                                                        sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700, fontSize: 10, height: 20 }}
                                                    />
                                                    <Chip 
                                                        label={`ติดต่อแล้ว: ${completed}`}
                                                        size="small"
                                                        sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 10, height: 20 }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-right">
                                                    <p className="text-2xl font-black text-indigo-600">{count}</p>
                                                    <p className="text-xs text-gray-500">คน</p>
                                                </div>
                                                <button
                                                    onClick={() => handleViewDetails(key, pos)}
                                                    className="p-2 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors"
                                                    title="ดูรายชื่อ"
                                                >
                                                    <EyeIcon className="h-5 w-5 text-indigo-600" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </AccordionDetails>
                    </Accordion>
                )
            })
        } catch (error) { return null }
    }, [dataRegistration, positionJob])

    const getLineColor = (index) => {
        const colors = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
        return colors[index % colors.length];
    };

    const renderCategoryToggles = () => (
        <div className="flex flex-wrap gap-4 mb-8 bg-white p-3 rounded-[2rem] shadow-sm border border-gray-100">
            <button 
                onClick={() => setReportCategory('registration')}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all ${reportCategory === 'registration' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-gray-500 hover:bg-gray-50'}`}
            >
                <UserGroupIcon className="h-5 w-5"/>
                สถิติการสมัครงาน
            </button>
            <button 
                onClick={() => setReportCategory('training')}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all ${reportCategory === 'training' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-100' : 'text-gray-500 hover:bg-gray-50'}`}
            >
                <AcademicCapIcon className="h-5 w-5"/>
                สถิติการส่งอบรม
            </button>
        </div>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            <div className="max-w-[1600px] mx-auto">
                {renderCategoryToggles()}

                {dataRegistration !== undefined ? (
                    <>
                        {reportCategory === 'registration' ? (
                            <Grid container spacing={4}>
                                {/* ฝั่งซ้าย: Workload & Accordion */}
                                <Grid item xs={12} lg={5}>
                                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 mb-6">
                                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Workload Status</h3>
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-end">
                                                <div>
                                                    <p className="text-4xl font-black text-gray-800">{stats.pending}</p>
                                                    <p className="text-sm text-red-500 font-bold flex items-center gap-1">
                                                        <ExclamationCircleIcon className="h-4 w-4"/> รอการติดต่อกลับ
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-green-600">{stats.percent}%</p>
                                                    <p className="text-xs text-gray-400 font-medium">ดำเนินการเสร็จสิ้น</p>
                                                </div>
                                            </div>
                                            <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                                                <div className="bg-indigo-600 h-full transition-all duration-1000" style={{ width: `${stats.percent}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    {manageData}
                                </Grid>

                                {/* ฝั่งขวา: Graphs */}
                                <Grid item xs={12} lg={7} className="space-y-6">
                                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
                                        <div className="flex items-center gap-3 mb-8">
                                            <div className="p-2.5 bg-emerald-600 rounded-2xl shadow-lg shadow-emerald-100">
                                                <PresentationChartLineIcon className="h-6 w-6 text-white"/>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-800">แนวโน้มตำแหน่งงานรายปี</h3>
                                                <p className="text-xs text-gray-400">ภาพรวมการเติบโตของผู้สมัครแยกตามตำแหน่ง</p>
                                            </div>
                                        </div>
                                        <div style={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <LineChart data={lineChartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 600}} />
                                                    <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12}} />
                                                    <ChartTooltip contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                                    <Legend iconType="circle" />
                                                    {positionJob.map((pos, index) => (
                                                        <Line key={pos} type="monotone" dataKey={pos} name={pos || "ไม่ระบุ"} stroke={getLineColor(index)} strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                                                    ))}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
                                        <div className="flex justify-between items-center mb-8">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 bg-indigo-600 rounded-2xl"><ChartBarIcon className="h-6 w-6 text-white"/></div>
                                                <h3 className="text-xl font-bold text-gray-800">เปรียบเทียบสถิติ</h3>
                                            </div>
                                            <div className="flex bg-gray-100 p-1 rounded-xl">
                                                {['position', 'year'].map(mode => (
                                                    <button key={mode} onClick={() => setViewMode(mode)} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${viewMode === mode ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'}`}>
                                                        {mode === 'position' ? 'ตามตำแหน่ง' : 'ตามปี'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={viewMode === 'position' ? chartDataByPosition : chartDataByYear} layout={viewMode === 'position' ? "vertical" : "horizontal"}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis type={viewMode === 'position' ? "number" : "category"} dataKey={viewMode === 'position' ? undefined : "name"} hide={viewMode === 'position'} axisLine={false} tickLine={false} />
                                                    <YAxis type={viewMode === 'position' ? "category" : "number"} dataKey={viewMode === 'position' ? "name" : undefined} width={viewMode === 'position' ? 120 : 40} axisLine={false} tickLine={false} />
                                                    <ChartTooltip contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                                    <Legend verticalAlign="top" align="right" />
                                                    <Bar name="มาใหม่" dataKey="มาใหม่" fill="#EF4444" radius={[5, 5, 5, 5]} barSize={20} />
                                                    <Bar name="ติดต่อแล้ว" dataKey="ติดต่อแล้ว" fill="#10B981" radius={[5, 5, 5, 5]} barSize={20} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[2rem] border border-dashed border-gray-300">
                                <AcademicCapIcon className="h-16 w-16 text-gray-300 mb-4" />
                                <h3 className="text-xl font-bold text-gray-400">ระบบสถิติการส่งอบรม</h3>
                                <p className="text-gray-400 italic">อยู่ระหว่างการพัฒนาข้อมูล...</p>
                            </div>
                        )}
                    </>
                ) : <Loading />}
            </div>

            {/* Dialog for Details */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 5 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', borderBottom: '1px solid #F1F5F9' }}>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-xl">
                            <UserGroupIcon className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                            <p>รายชื่อผู้สมัครงาน</p>
                            <p className="text-sm font-normal text-gray-500">
                                ปี {selectedDetails?.year} - {selectedDetails?.position}
                            </p>
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                        {selectedDetails?.data.map((person, index) => (
                            <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <p className="font-bold text-gray-900">{person.name}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {new Date(person.timestamp).toLocaleString('th-TH', {
                                                day: '2-digit', month: 'short', year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                    <Chip 
                                        label={person.check === "yes" ? "ติดต่อแล้ว" : "รอติดต่อ"}
                                        size="small"
                                        sx={{ 
                                            bgcolor: person.check === "yes" ? '#D1FAE5' : '#FEF3C7',
                                            color: person.check === "yes" ? '#065F46' : '#92400E',
                                            fontWeight: 700,
                                            fontSize: 10
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-gray-500">อายุ:</span> <span className="font-semibold">{person.age} ปี</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">เบอร์:</span> <span className="font-mono font-semibold">{person.phone}</span>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="text-gray-500">ตำแหน่ง:</span> <span className="font-semibold">{person.positionjob}</span>
                                    </div>
                                    {person.remark && (
                                        <div className="col-span-2 mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                                            <span className="text-xs text-amber-600 font-semibold">หมายเหตุ:</span>
                                            <p className="text-xs text-amber-900 mt-1">{person.remark}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 p-3 bg-indigo-50 rounded-xl border border-indigo-200">
                        <p className="text-sm font-bold text-indigo-900">
                            รวมทั้งหมด: {selectedDetails?.data.length} คน
                        </p>
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #F1F5F9' }}>
                    <Button 
                        onClick={() => setOpenDialog(false)}
                        sx={{ 
                            color: '#64748B', 
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#F1F5F9' }
                        }}
                    >
                        ปิด
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default Report;
